//TODO: Eventually will change conference type to follow what backend sends instead of Global, Regional

import { isNativeNotWeb } from '@/utils/platform-check';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import { setMixpanelUserProperty } from '@/utils/mixpanel';

export const CONFERENCE_TYPES = {
  GLOBAL: 'Global',
  REGIONAL: 'Regional',
  GIC: 'GIC',
};

// TODO: current active conf year
const CURRENT_YEAR = 2025;

// Helper functions to work with conferences array
/**
 * Get the most recent conference year from conferences array
 * @param {Array} conferences - Array of conference objects {id, type, year}
 * @returns {number|null} - Most recent year or null
 */
const getMostRecentYear = conferences => {
  if (!conferences || !Array.isArray(conferences) || conferences.length === 0) {
    return null;
  }
  return Math.max(...conferences.map(c => c.year));
};

/**
 * Get conferences for a specific year
 * @param {Array} conferences - Array of conference objects
 * @param {number} year - Year to filter by
 * @returns {Array} - Filtered conferences
 */
const getConferencesByYear = (conferences, year) => {
  if (!conferences || !Array.isArray(conferences)) return [];
  return conferences.filter(c => c.year === year);
};

/**
 * Check if user has a specific conference type
 * @param {Array} conferences - Array of conference objects
 * @param {string} type - Conference type (GPC, GPCR, GIC)
 * @param {number|null} year - Optional year filter (if null, checks all years)
 * @returns {boolean}
 */
const hasConferenceType = (conferences, type, year = null) => {
  if (!conferences || !Array.isArray(conferences)) return false;
  const filtered = year ? getConferencesByYear(conferences, year) : conferences;
  return filtered.some(c => c.type === type);
};

/**
 * Check if user has a specific conference type, prioritizing current year but falling back to any year
 * This prioritizes TYPE over YEAR - if user has the type in any year, returns true
 * Prefers current year if available, but doesn't require it
 * @param {Array} conferences - Array of conference objects
 * @param {string} type - Conference type (GPC, GPCR, GIC)
 * @returns {boolean}
 */
const hasConferenceTypeAnyYear = (conferences, type) => {
  if (!conferences || !Array.isArray(conferences)) return false;
  // Check current year first (preferred)
  if (hasConferenceType(conferences, type, CURRENT_YEAR)) {
    return true;
  }
  // Fall back to any year
  return hasConferenceType(conferences, type);
};

/**
 * Derive confYear-like string from conferences array for backward compatibility
 * Uses most recent year and available types
 * @param {Array} conferences - Array of conference objects
 * @returns {string|null} - confYear string like "2025-GR" or null
 */
const deriveConfYearFromConferences = conferences => {
  if (!conferences || !Array.isArray(conferences) || conferences.length === 0) {
    return null;
  }
  const mostRecentYear = getMostRecentYear(conferences);
  const currentYearConfs = getConferencesByYear(conferences, mostRecentYear);

  const hasGPC = currentYearConfs.some(c => c.type === 'GPC');
  const hasGPCR = currentYearConfs.some(c => c.type === 'GPCR');

  if (hasGPC && hasGPCR) {
    return `${mostRecentYear}-GR`;
  } else if (hasGPCR) {
    return `${mostRecentYear}-R`;
  } else if (hasGPC) {
    return `${mostRecentYear}-G`;
  }

  // If only GIC or other types, return year only (fallback)
  return `${mostRecentYear}-G`;
};

// This would be used to check if the user is on the GPCR page
export const isGPCRPage = () => {
  return window.location.pathname.includes('gpcr');
};

export const isGICPage = () => {
  return window.location.pathname.includes('gic');
};

export const getConferenceType = () => {
  if (isGPCRPage()) return 'Regional';
  if (isGICPage()) return 'GIC';
  return 'Global';
};

// Conference.type as stored ('GPC' | 'GPCR' | 'GIC') → the conference-view names
// the rest of the app uses ('Global' | 'Regional' | 'GIC').
const CONF_TYPE_BY_RECORD_TYPE = { GIC: 'GIC', GPCR: 'Regional' };
const conferenceRecordToType = conference => CONF_TYPE_BY_RECORD_TYPE[conference?.type] || 'Global';

/**
 * The one conference to treat as "live" when several are flagged currentConference.
 *
 * Highest id wins. id is autoincrement, so it stands in for "most recently
 * created", and it's the same rule the API's resolveSingleCurrentConference uses
 * (Math.max of the current ids) — so when that API-side normalisation is
 * reverted and the app starts seeing several current conferences, the answer
 * doesn't change.
 *
 * TODO: id is a proxy for time, not time itself. A conference row backfilled
 * later gets a higher id than an earlier-created one for a later conference.
 * When there's time, add a real date to the Conference model (it currently has
 * only year, signupEndDate, createdAt, updatedAt — no conference start date)
 * and sort on that instead.
 */
export const getCurrentConference = (conferences, confType = null) => {
  if (!Array.isArray(conferences)) return null;
  return conferences
    .filter(c => c.currentConference === true)
    .filter(c => !confType || conferenceRecordToType(c) === confType)
    .reduce((latest, c) => (!latest || c.id > latest.id ? c : latest), null);
};

// Absent by default. With no conference records we don't know whether the
// delegate is attending, and the two wrong answers aren't equally cheap:
// guessing "present" opens every `!isAbsentAlumni` gate in the app (hero shape,
// QR / Experiences / PhotoDrop buttons, seating, livevoice), so an off-season
// delegate sees in-season UI that shouldn't exist. Guessing "absent" just holds
// those back for the moment it takes the profile to load.
//
// This is the state whenever the router guard swallows an auth or profile error
// and lets navigation continue with an unpopulated store (router/index.js:491).
//
// The old confYear fallback is gone: confYear is deprecated, and it depended on
// the hardcoded CURRENT_YEAR.
export const isAbsentAlumni = (conferences = null) => {
  if (!Array.isArray(conferences) || conferences.length === 0) return true;

  // Being current somewhere isn't enough — a conference that's actually running
  // right now has to match the variant currently being viewed. Otherwise a GIC
  // delegate viewing the GPC page reads as "present" there too.
  //
  // Asked as "is ANY current conference of the type I'm looking at", so several
  // conferences flagged currentConference need no tie-break: a delegate live in
  // both GPC and GIC is genuinely present on both, and picking a single winner
  // would wrongly call them absent on one of them.
  return !getCurrentConference(conferences, getConferenceType());
};

// check if GR is in the confYear example: 2024-GR, 2025-GR
// split by - and check if GR is in the second part
// This would be used to check if the user can view both GPC and GPCR
// Now supports conferences array - checks if user has both GPC and GPCR (prioritizes current year, but any year works)
// TYPE matters more than YEAR for view selection
export const haveBothGPCandGPCR = (confYear, conferences = null) => {
  // Priority: use conferences array if available
  if (conferences && Array.isArray(conferences) && conferences.length > 0) {
    // Check if user has both types - prefer current year but fall back to any year
    return (
      hasConferenceTypeAnyYear(conferences, 'GPC') && hasConferenceTypeAnyYear(conferences, 'GPCR')
    );
  }

  // Fallback to confYear
  if (!confYear) return false;
  const parts = confYear.split('-');
  return parts[1] === 'GR';
};

/**
 * Check if user has multiple conference types (needs switcher)
 * Returns true if user has 2+ of: GPC, GPCR, GIC
 * TYPE matters more than YEAR for view selection
 * @param {string} confYear - Legacy confYear string
 * @param {Array} conferences - Array of conference objects
 * @returns {boolean}
 */
export const hasMultipleConferenceTypes = (confYear, conferences = null) => {
  // Priority: use conferences array if available
  if (conferences && Array.isArray(conferences) && conferences.length > 0) {
    const hasGPC = hasConferenceTypeAnyYear(conferences, 'GPC');
    const hasGPCR = hasConferenceTypeAnyYear(conferences, 'GPCR');
    const hasGIC = hasConferenceTypeAnyYear(conferences, 'GIC');

    // Count how many types user has
    const typeCount = [hasGPC, hasGPCR, hasGIC].filter(Boolean).length;
    return typeCount >= 2;
  }

  // Fallback to confYear - if it has "GR", user has both GPC and GPCR
  if (!confYear) return false;
  const parts = confYear.split('-');
  return parts[1] === 'GR';
};

export const isDelegateGPCROnly = (confYear, conferences = null) => {
  // Priority: use conferences array if available
  if (conferences && Array.isArray(conferences) && conferences.length > 0) {
    // TYPE matters more than YEAR - check if user has GPCR (any year) and NOT GPC/GIC (any year)
    return (
      hasConferenceTypeAnyYear(conferences, 'GPCR') &&
      !hasConferenceTypeAnyYear(conferences, 'GPC') &&
      !hasConferenceTypeAnyYear(conferences, 'GIC')
    );
  }

  // Fallback to confYear
  if (!confYear) return false;
  const parts = confYear.split('-');
  return parts[1] === 'R';
};

export const isDelegateGPCOnly = (confYear, conferences = null) => {
  // Priority: use conferences array if available
  if (conferences && Array.isArray(conferences) && conferences.length > 0) {
    // TYPE matters more than YEAR - check if user has GPC (any year) and NOT GPCR/GIC (any year)
    return (
      hasConferenceTypeAnyYear(conferences, 'GPC') &&
      !hasConferenceTypeAnyYear(conferences, 'GPCR') &&
      !hasConferenceTypeAnyYear(conferences, 'GIC')
    );
  }

  // Fallback to confYear
  if (!confYear) return false;
  const parts = confYear.split('-');
  return parts[1] === 'G';
};

// Renamed from 'app-conftype' to force every existing saved preference to
// reset — same trick used previously (gpc2025-app-conftype -> gic3-app-conftype
// -> app-conftype). Without this, a value written under the old buggy default
// logic would still validate and mask the getCurrentConferenceType fix above.
const CONF_VIEW_KEY_BASE = 'selected-conftype';

// id of the conference flagged currentConference, or 0 when none is available
const getCurrentConferenceId = conferences =>
  conferences?.find(c => c.currentConference === true)?.id ?? 0;

// Scope the saved conf-view preference to the current conference, so a new
// conference cycle (new currentConference id) is not pinned to a stale choice.
const getConfViewKey = (conferences = null) =>
  `${CONF_VIEW_KEY_BASE}-${getCurrentConferenceId(conferences)}`;

// return either "Global", "Regional", or "GIC", or null if no preference saved
// This would be used to check the current view of the app based on user's selection in conference selection
export const getSelectedConferenceView = (conferences = null) => {
  const savedConfView = localStorage.getItem(getConfViewKey(conferences));
  return savedConfView || null; // null instead of 'Global' — let getCurrentConferenceType decide default
};

export const setConferenceView = (confType, conferences = null) => {
  // confType can be "Global", "Regional", or "GIC"
  // Guard against persisting a choice the delegate doesn't actually have
  // access to — a stale/invalid write here could later look like a legitimate
  // explicit choice once access changes. Pre-login/deep-link callers pass
  // conferences=null (no profile fetched yet) and are exempt from this check.
  if (conferences && !validateConferenceAccess(confType, conferences)) {
    return;
  }
  localStorage.setItem(getConfViewKey(conferences), confType);
  if (isNativeNotWeb()) {
    FirebaseAnalytics.setUserProperty({
      name: 'selected_conference_type',
      value: confType,
    });
  }
  setMixpanelUserProperty({ selected_conference_type: confType });
};

/**
 * Check if user has access to a specific conference type
 * @param {string} confType - 'Global' | 'Regional' | 'GIC'
 * @param {Array} conferences - User's conferences from profile
 * @returns {boolean}
 */
export const validateConferenceAccess = (confType, conferences) => {
  if (!conferences || !Array.isArray(conferences)) return false;
  const typeKey = confType === 'Regional' ? 'GPCR' : confType === 'Global' ? 'GPC' : 'GIC';
  return hasConferenceTypeAnyYear(conferences, typeKey);
};

// No saved preference (or invalid) — default to whichever conference is
// actually running right now, if the delegate has access to it. Otherwise
// (absent alumni, or no access to what's live) fall back to a fixed priority
// among the types they do have.
const resolveDefaultConferenceType = conferences => {
  const liveType = getLatestConferenceType(conferences);
  if (validateConferenceAccess(liveType, conferences)) return liveType;

  const DEFAULT_CONF_PRIORITY = ['Global', 'GIC', 'Regional'];
  for (const type of DEFAULT_CONF_PRIORITY) {
    if (validateConferenceAccess(type, conferences)) return type;
  }
  return 'Global';
};

// return either "Global", "Regional", or "GIC"
// This would be used to check the current view of the app based on user's conference type
// Now supports conferences array - determines type based on available conferences
// TYPE matters more than YEAR - prioritizes conference type availability over year
export const getCurrentConferenceType = (confYear, conferences = null) => {
  // Priority: use conferences array if available
  if (conferences && Array.isArray(conferences) && conferences.length > 0) {
    const hasGIC = hasConferenceTypeAnyYear(conferences, 'GIC');
    const hasGPCR = hasConferenceTypeAnyYear(conferences, 'GPCR');
    const hasGPC = hasConferenceTypeAnyYear(conferences, 'GPC');

    // Single-type users — no choice needed
    if (hasGIC && !hasGPC && !hasGPCR) return 'GIC';
    if (hasGPCR && !hasGPC && !hasGIC) return 'Regional';
    if (hasGPC && !hasGPCR && !hasGIC) return 'Global';

    // Multi-type users — check saved preference, validate with reusable access check
    const saved = getSelectedConferenceView(conferences);
    if (saved && validateConferenceAccess(saved, conferences)) {
      return saved; // Valid — user still has access
    }

    return resolveDefaultConferenceType(conferences);
  }

  // Fallback to confYear logic (unchanged)
  if (isDelegateGPCROnly(confYear)) return 'Regional';
  if (isDelegateGPCOnly(confYear)) return 'Global';
  // if delegate is returning delegate from GPC, check most recent selected view
  const saved = getSelectedConferenceView();
  return saved || 'Global'; // legacy fallback still defaults to Global
};

export const DEFAULT_CONFERENCE_TYPE = 'Regional';

export const getLatestConferenceType = conferences => {
  const current = getCurrentConference(conferences);
  if (!current) return 'Global';
  return conferenceRecordToType(current);
};

// New helper functions for GIC support
/**
 * Check if user has GIC conference
 * Prioritizes current year but falls back to any year (TYPE over YEAR)
 * @param {Array} conferences - Array of conference objects
 * @param {number|null} year - Optional year filter (if null, uses anyYear logic)
 * @returns {boolean}
 */
export const hasGIC = (conferences, year = null) => {
  if (year === null) {
    return hasConferenceTypeAnyYear(conferences, 'GIC');
  }
  return hasConferenceType(conferences, 'GIC', year);
};

/**
 * Check if user has GPC conference
 * Prioritizes current year but falls back to any year (TYPE over YEAR)
 * @param {Array} conferences - Array of conference objects
 * @param {number|null} year - Optional year filter (if null, uses anyYear logic)
 * @returns {boolean}
 */
export const hasGPC = (conferences, year = null) => {
  if (year === null) {
    return hasConferenceTypeAnyYear(conferences, 'GPC');
  }
  return hasConferenceType(conferences, 'GPC', year);
};

/**
 * Check if user has GPCR conference
 * Prioritizes current year but falls back to any year (TYPE over YEAR)
 * @param {Array} conferences - Array of conference objects
 * @param {number|null} year - Optional year filter (if null, uses anyYear logic)
 * @returns {boolean}
 */
export const hasGPCR = (conferences, year = null) => {
  if (year === null) {
    return hasConferenceTypeAnyYear(conferences, 'GPCR');
  }
  return hasConferenceType(conferences, 'GPCR', year);
};

/**
 * Check if user has ONLY GIC conference (no GPC or GPCR)
 * TYPE matters more than YEAR - checks any year
 * @param {Array} conferences - Array of conference objects
 * @returns {boolean}
 */
export const isDelegateGICOnly = conferences => {
  if (!conferences || !Array.isArray(conferences) || conferences.length === 0) {
    return false;
  }
  return (
    hasConferenceTypeAnyYear(conferences, 'GIC') &&
    !hasConferenceTypeAnyYear(conferences, 'GPC') &&
    !hasConferenceTypeAnyYear(conferences, 'GPCR')
  );
};

/**
 * Get all conference types user has access to
 * @param {Array} conferences - Array of conference objects
 * @param {number|null} year - Optional year filter
 * @returns {Array} - Array of conference type strings
 */
export const getAvailableConferenceTypes = (conferences, year = null) => {
  if (!conferences || !Array.isArray(conferences)) return [];
  const filtered = year ? getConferencesByYear(conferences, year) : conferences;
  const types = new Set(filtered.map(c => c.type));
  return Array.from(types);
};

/**
 * Get available conference view options for switcher
 * Returns array of view options user can switch between
 * @param {string} confYear - Legacy confYear string
 * @param {Array} conferences - Array of conference objects
 * @returns {Array} - Array of view option objects {value: "Global"|"Regional"|"GIC", name: string}
 */
export const getAvailableConferenceViews = (confYear, conferences = null) => {
  const views = [];

  // Priority: use conferences array if available
  if (conferences && Array.isArray(conferences) && conferences.length > 0) {
    if (hasConferenceTypeAnyYear(conferences, 'GPC')) {
      views.push({ value: 'Global', name: 'GPC' });
    }
    if (hasConferenceTypeAnyYear(conferences, 'GPCR')) {
      views.push({ value: 'Regional', name: 'GPCR' });
    }
    if (hasConferenceTypeAnyYear(conferences, 'GIC')) {
      views.push({ value: 'GIC', name: 'GIC' });
    }
    return views;
  }

  // Fallback to confYear
  if (confYear) {
    const parts = confYear.split('-');
    const types = parts[1] || '';

    if (types.includes('G')) {
      views.push({ value: 'Global', name: 'GPC' });
    }
    if (types.includes('R')) {
      views.push({ value: 'Regional', name: 'GPCR' });
    }
  }

  return views;
};

/**
 * Convenience function to get conference info from profile state
 * Automatically uses conferences array if available, falls back to confYear
 * @param {Object} profile - Profile state object with confYear and/or conferences
 * @returns {Object} - { confYear, conferences, useConferences }
 */
export const getConferenceInfo = profile => {
  const conferences =
    profile?.conferences && Array.isArray(profile.conferences) ? profile.conferences : null;
  const confYear = profile?.confYear || null;

  // If conferences array exists and has data, use it; otherwise use confYear
  const useConferences = conferences && Array.isArray(conferences) && conferences.length > 0;

  return {
    confYear: useConferences ? deriveConfYearFromConferences(conferences) : confYear,
    conferences: conferences,
    useConferences: useConferences,
  };
};

/**
 * Convert conference type display name to URL prefix
 * 'Regional' → '/gpcr', 'GIC' → '/gic', 'Global' → ''
 */
export const confTypeToPrefix = confType => {
  if (confType === 'Regional') return '/gpcr';
  if (confType === 'GIC') return '/gic';
  return '';
};

/**
 * Strip conference prefix from a path
 * '/gpcr/home' → '/home', '/gic/schedule' → '/schedule', '/home' → '/home'
 */
export const stripConferencePrefix = path => {
  if (path.startsWith('/gpcr/')) return path.slice(5);
  if (path.startsWith('/gic/')) return path.slice(4);
  return path;
};

/**
 * Extract conference type from a URL path based on prefix
 * '/gpcr/home' → 'Regional', '/gic/schedule' → 'GIC'
 * '/home' → null (unprefixed — no explicit conference intent)
 */
export const extractConfTypeFromPath = path => {
  if (path.startsWith('/gpcr/') || path === '/gpcr') return 'Regional';
  if (path.startsWith('/gic/') || path === '/gic') return 'GIC';
  return null; // unprefixed — don't assume a conference type
};

/**
 * Extract conference type from a route prefix string
 * '' → 'Global', '/gpcr' → 'Regional', '/gic' → 'GIC'
 */
export const extractConfTypeFromPrefix = prefix => {
  if (prefix === '/gpcr') return 'Regional';
  if (prefix === '/gic') return 'GIC';
  return 'Global';
};
